<template>
  <section class="promo__codes-form-wrap">
    <Modal
      v-model:show="active"
      @close="close"
    >
      <template v-slot:header>
        <h5 class="modal-title">
          {{ $t('promoCodes.addPromocodes.editDomain') }}
        </h5>
      </template>
      <div>
        <form @keyup.enter="send">
          <div class="select-wrapper mb-8">
            <UiSelect
              v-model="offer"
              :label="$t('promoCodes.addPromocodes.offer')"
              :placeholder="$t('promoCodes.addPromocodes.selectOffer')"
              :list="offers"
              is-search
              @change="offerSelect = true"
            />
            <span
              v-if="!offerSelect && sending"
              class="text-danger !tw-text-left"
            >
              {{ $t('promoCodes.addPromocodes.selectOffer') }}
            </span>
          </div>

          <ElPopover 
            ref="popover" 
            v-model:visible="warningOpen" 
            placement="top-end" 
            trigger="click" 
            width="320"
          >
            <p>{{ $t('promoCodes.addPromocodes.changeSubId') }}</p>
            <button
              class="btn btn-outline-primary btn-md"
              @click="warningOpen = false"
            >
              {{ $t('offerRequest.btnCancel') }}
            </button>
            <button
              class="btn btn-primary btn-md !tw-ml-1rem"
              @click="
                confirmChanges();
                warningOpen = false;
              "
            >
              {{ $t('offerRequest.btnConfirm') }}
            </button>
            
            <template v-slot:reference>
              <div />
            </template>
          </ElPopover>

          <div class="select-wrapper mb-8">
            <!-- select traffic source -->
            <UiSelect
              v-model="traffic_source_id"
              :label="$t('trafficSources.trafficSource')"
              :placeholder="$t('offerRequest.noTrafficSources')"
              :list="sources"
              is-search
              @change="changeSource"
            />
          </div>

          <div class="row row-wrap__subid">
            <div
              v-for="(item, i) in form.subids"
              :key="i"
              class="col-6 col-6__add-subid mb-12 "
            >
              <UiInputSubid
                v-model="item.value"
                :label="`SubID ${i + 1}`"
                :placeholder="$t('trafficSources.postbacks.notSpecified')"
                :can-delete="form.subids.indexOf(item) === form.subids.length - 1 && form.subids.length > 1"
                @delete="popSubIds"
              />
            </div>
            <div class="col-6 !tw-flex !tw-items-end col-6__add-subid">
              <button
                :disabled="form.subids.length >= limitSubIds"
                href="#"
                class="btn btn-block btn-outline-primary btn-sm mb-12"
                @click.prevent="form.subids.length < limitSubIds && addSubId()"
              >
                + {{ $t('promoCodes.addPromocodes.addAnotherSubid') }}
              </button>
            </div>
          </div>
        </form>
      </div>
      <template v-slot:footer>
        <div class="!tw-text-right !tw-flex !tw-items-baseline">
          <a
            class="btn btn-outline-primary btn-sm mb-4"
            @click="close"
          >
            {{ $t('promoCodes.addPromocodes.cancel') }}
          </a>
          <a
            class="btn btn-primary btn-sm !tw-ml-1rem"
            @click="send"
          >
            <span v-if="!$store.state.errors.loading">
              {{ $t('profile.buttons.save') }}
            </span>
            <i
              v-if="$store.state.errors.loading"
              class="fas fa-spinner spinner-hide"
            />
          </a>
        </div>
      </template>
    </Modal>
  </section>
</template>

<script>
import { Modal } from 'src/components';
import _ from '@/lib/lodash';

export default {
  components: {
    Modal,
  },
  props: ['offsetParams'],
  emits: ['confirm'],
  data() {
    return {
      limitSubIds: 5,
      warningOpen: false,
      active: false,
      offer: '',
      id: '',
      changed: false,
      offerSelect: false,
      sending: false,
      traffic_source_id: '',
      oldTSid: '',
      form: {
        promocode: '',
        subids: [{ value: null }],
      },
    };
  },

  computed: {
    offers() {
      const offers = this.$store.state.promocode.offers.map((e) => ({
        text: e.name,
        id: e.id,
      }));
      return [{ id: '', text: this.$t('promoCodes.addPromocodes.selectOffer') }].concat(offers);
    },
    sources() {
      const sources = this.$store.state.trafficSource.list.map((e) => ({
        id: e.data.id,
        text: e.name,
      }));
      return [{ id: '', text: this.$t('offerRequest.noTrafficSources') }].concat(sources);
    },
  },
  activated() {
    this.$store.dispatch('getPromocodeOffersLazy');
  },

  watch: {
    traffic_source_id(value, oldValue) {
      this.oldTSid = oldValue;
    },
  },

  methods: {
    selectOffer(v) {
      this.offer = v.id;
      this.offerSelect = true;
    },
    changeSource(value) {
      if (!value) {
        // if no traffic source selected clear subids
        this.form.subids = [{ value: null }];
        return;
      }
      // subids from promocodes
      let promocodeSubIds = this.$store.state.promocode.promocodes.filter((e) => e.id === this.id)[0];
      promocodeSubIds = _.pick(promocodeSubIds, ['subid1', 'subid2', 'subid3', 'subid4', 'subid5']);

      // subids from ts
      let source = this.$store.state.trafficSource.list.filter((e) => e.data.id === value)[0];
      source = _.pick(source, ['subid1', 'subid2', 'subid3', 'subid4', 'subid5']);

      // subids from old ts
      let oldSource = this.$store.state.trafficSource.list.filter((e) => e.data.id === this.oldValue)[0];
      oldSource = _.pick(oldSource, ['subid1', 'subid2', 'subid3', 'subid4', 'subid5']);

      // take current subids
      const subids = {};
      for (let i = 0; i < this.limitSubIds; i++) {
        const key = `subid${i + 1}`;
        subids[key] = this.form.subids[i] ? this.form.subids[i].value : null;
      }

      // if current subids is equal with subids from promocodes or trafficsources
      const templateNulleble = {};
      for (let i = 0; i < this.limitSubIds; i++) {
        const key = `subid${i + 1}`;
        templateNulleble[key] = null;
      }
      if (_.isEqual({}, oldSource)) oldSource = templateNulleble;

      if (_.isEqual(subids, oldSource)) {
        this.form.subids = [];
        this.addSubids(source);
      } else {
        // show warning
        this.warningOpen = true;
      }
      this.oldValue = value;
    },
    confirmChanges() {
      this.form.subids = [];
      const source = this.$store.state.trafficSource.list.filter((e) => e.data.id === this.traffic_source_id)[0];
      this.addSubids(source);
    },
    addSubids(source) {
      if (!source) {
        this.form.subids = [{ value: null }];
        return;
      }
      this.form.subids = [];
      // Push subids
      Object.keys(source).map((k) => {
        if (k.startsWith('subid')) {
          const subid_index = parseInt(k.replace('subid', '')) - 1;
          if (source[k]) {
            this.form.subids.push({
              value: source[k],
            });
          } else {
            this.form.subids.push({
              value: null,
            });
          }
        }
      });
      // reduce subids
      for (let i = this.form.subids.length - 1; i >= 1; i--) {
        if (!this.form.subids[i].value) this.form.subids.pop();
        else break;
      }
    },

    async open(opts) {
      await this.$store.dispatch('getTrafficSources', {});
      this.offer = opts.affiliateOfferId;
      this.id = opts.id;
      this.form.promocode = opts.promo_code;
      this.traffic_source_id = opts.traffic_source_id;
      this.active = true;
      this.offerSelect = true;
      this.form.subids = opts.subids;
    },
    close() {
      this.form.subids = [{ value: null }];
      this.active = false;
      this.form.promocode = '';
      this.changed = false;
      this.traffic_source_id = '';
      this.offer = '';
      this.offerSelect = false;
      this.sending = false;
      this.warningOpen = false;
      this.oldValue = {};
    },
    send() {
      this.sending = true;
      this.changed = true;
      if (!this.offer) {
        this.offerSelect = false;
        return;
      }

      const params = {
        traffic_source_id: this.traffic_source_id,
        affiliate_offer_id: this.offer,
      };

      this.form.subids.forEach((cur, i) => {
        params[`subid${parseInt(i) + 1}`] = cur.value;
      });

      this.$store
        .dispatch('putDedicatedDomain', { id: this.id, params })
        .then((res) => {
          this.close();
          this.$alert.success(this.$t('promoCodes.alerts.wasUpdated2'));
        })
        .catch((err) => {
          this.$alert.error(err.response.data.message);
        })
        .finally(() => {
          this.$emit('confirm');
        });
    },

    addSubId() {
      this.form.subids.push({
        value: null,
      });
    },
    popSubIds(item) {
      this.form.subids.pop();
    },
  },
};
</script>

<style lang="scss">
// vue3-migration -- '@/assets/theme/common-page-styles.scss'
// @import 'src/assets/theme/default/promocodes/addpromocode';
</style>
